import React from 'react';
import {
	type AutoListCommonExtendProps,
	AutoListField,
} from '@netx/core-components/AutoListField';
import {useAutoList} from '@netx/core-next/hooks/autoList/useAutoList';
import {i18n} from '@netx/core-next/locales/i18n-instance';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import {getPeople} from '../../api';
import {type IPerson} from '../../schema';
import {PersonListItem} from '../PersonListItem';

export {type IPerson} from '../../schema';

export type IPersonOption = Pick<IPerson, 'id' | 'name'>;

export type PersonAutoListRef = React.ForwardedRef<HTMLDivElement>;

const DEFAULT_NAME = 'person-auto-list';

const _getPeople = async ({
	query,
	params,
}: {
	query: string;
	params?: Record<string, any>;
}) => {
	return (
		await getPeople({
			filters: {
				name: query,
			},
		})
	).results;
};

export interface PersonAutoListProps<
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
> extends AutoListCommonExtendProps<
	IPersonOption,
	Multiple,
	DisableClearable,
	true
> {}

/**
 * Default get option label fn
 * @param option Option
 */
const defaultGetOptionLabel = (option?: IPersonOption | string) =>
	typeof option === 'string' ? option : (option?.name ?? '');

/**
 * Default get option selected fn
 * @param option Option
 * @param value Value
 */
const defaultGetOptionSelected = (
	option: IPersonOption,
	value?: IPersonOption,
) =>
	Array.isArray(value)
		? value.some((v) => v.id === option.id)
		: option.id === value?.id;

/** should option be disabled */
const defaultGetOptionDisabled = (option: IPersonOption) => false; //option.id === '';

const params = {};

export const PersonAutoListPrimitive = <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	{
		filterOptions,
		getOptionLabel = defaultGetOptionLabel,
		getOptionSelected = defaultGetOptionSelected,
		getOptionDisabled = defaultGetOptionDisabled,
		onClose,
		onInputChange,
		onOpen,
		...props
	}: PersonAutoListProps<Multiple, DisableClearable>,
	ref: PersonAutoListRef,
) => {
	const {loadingOptions, query, queryStr, setQueryStr, setIsOpen} = useAutoList(
		{
			params,
			isQueryValid: (query) => true,
			filterOptions,
			queryFn: _getPeople,
		},
	);

	const placeholder =
		props.multiple !== true ? i18n.t('i18n.selectPerson') : undefined;

	return (
		<AutoListField<IPersonOption, Multiple, DisableClearable, true>
			ref={ref}
			hasSecondaryText
			blurOnSelect={false}
			label={i18n.t('i18n.addPerson')}
			loadingOptions={loadingOptions}
			multiple={false as Multiple}
			name={DEFAULT_NAME}
			placeholder={placeholder}
			isOptionEqualToValue={getOptionSelected}
			renderOption={(props, option, state) => (
				<PersonListItem {...props} person={option} />
			)}
			inputValue={queryStr}
			{...props}
			filterOptions={(options, params) => {
				const _options = filterOptions
					? filterOptions([...options])
					: [...options];
				if (props.freeSolo && params.inputValue !== '') {
					_options.unshift({
						id: '',
						name: `${params.inputValue}`,
					});
				}
				return _options;
			}}
			options={query.data ?? []}
			getOptionDisabled={getOptionDisabled}
			getOptionLabel={getOptionLabel}
			onClose={splitEventHandler(onClose, () => setIsOpen(false))}
			onOpen={splitEventHandler(onOpen, () => setIsOpen(true))}
			onInputChange={splitEventHandler(
				onInputChange,
				(event, value, reason) => {
					setQueryStr(value);
				},
			)}
		/>
	);
};

/**
 * User auto list field
 */
export const PersonAutoList = React.forwardRef(PersonAutoListPrimitive) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	props: PersonAutoListProps<Multiple, DisableClearable>,
) => JSX.Element;

export default PersonAutoList;
